import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { HANDLED_ERRORS, SKIP_AUTH_REDIRECT } from '../../../shared/configs/http-context.config';
import { CustomError } from '../../../shared/models/api-response';
import { HelperService } from '../../services/helper/helper.service';
import { AuthService } from '../../services/http/auth/auth.service';
import { MaintenanceService } from '../../services/maintenance/maintenance.service';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private dialog: MatDialog,
    private helperService: HelperService,
    private maintenanceService: MaintenanceService,
    private router: Router,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(catchError((err) => this.handleError(request, err)));
  }

  private handleError(request: HttpRequest<unknown>, httpError: HttpErrorResponse) {
    const customError: CustomError = {
      statusCode: httpError.status,
      statusText: httpError.statusText,
      message: httpError.error?.message,
      errors: httpError.error?.errors,
      originalError: httpError,
    };

    // Check for MAINTENANCE_MODE error code in multiple possible locations
    const errorCode =
      httpError.error?.code ||
      httpError.error?.error_code ||
      httpError.error?.errorCode ||
      (httpError.error as any)?.data?.code;

    // Also check if message contains MAINTENANCE_MODE as fallback
    // const messageContainsMaintenance = httpError.error?.message?.includes('MAINTENANCE_MODE') || customError.message?.includes('MAINTENANCE_MODE');
    // Check for 503 Service Unavailable status (common for maintenance)
    // const isServiceUnavailable = httpError.status === 503;
    // if (errorCode === 'MAINTENANCE_MODE' || messageContainsMaintenance || isServiceUnavailable) {
    if (errorCode === 'MAINTENANCE_MODE') {
      const maintenanceMessage =
        httpError.error?.message || 'System is under maintenance. Please try again later.';

      this.maintenanceService.setMaintenanceMode(true, maintenanceMessage);
      this.dialog.closeAll();

      setTimeout(() => {
        const currentUrl = this.router.url;
        const isOnMaintenanceDisplayPage =
          currentUrl === '/maintenance' || currentUrl.startsWith('/maintenance?');
        const isOnMaintenanceManagementPage =
          currentUrl.includes('/quest/global/maintenance') ||
          currentUrl.includes('/global/maintenance');

        if (!isOnMaintenanceDisplayPage && !isOnMaintenanceManagementPage) {
          this.router.navigate(['/maintenance']);
        }
      }, 100);

      return throwError(customError);
    }
    // Clear maintenance mode if it was previously set but error is not maintenance
    if (this.maintenanceService.currentIsMaintenanceMode) {
      this.maintenanceService.clearMaintenanceMode();
    }

    const skipAuthRedirect = request.context.get(SKIP_AUTH_REDIRECT);

    // Logout if 401 Unauthorized
    if (customError.statusCode === 401 && !skipAuthRedirect) {
      this.dialog.closeAll();
      this.authService.logoutLocally();
      this.router.navigate(['/login'], {
        queryParams: {
          redirectUrl: this.router.url,
        },
      });
    }

    const isRedirect = this.activatedRoute.snapshot.queryParamMap.get('isRedirect') != null;

    // If 403 Forbidden response returned from api navigate to access denied if not due to redirect
    if (customError.statusCode === 403) {
      this.dialog.closeAll();
      this.router.navigate([isRedirect ? '/quest' : '/access-denied']);
    }

    // Show message if error is not handled in component and if not redirected from login
    // Don't show error messages if maintenance mode is active
    if (
      !request.context.get(HANDLED_ERRORS).includes(customError.statusCode) &&
      !isRedirect &&
      !this.maintenanceService.currentIsMaintenanceMode &&
      !(skipAuthRedirect && customError.statusCode === 401)
    ) {
      let message: string;
      switch (customError.statusCode) {
        case 401:
          message = environment.messages.sessionTimeOut;
          break;

        case 500:
          message = environment.messages.error;
          break;

        default:
          message = customError.message ?? environment.messages.error;
          break;
      }
      this.helperService.showMessage('error', message);
    }

    return throwError(customError);
  }
}
